import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BITRIX, ONLINE_PLATFORM_URL, PATHS, externalRecourse } from 'common/constants';
import { changeLang } from 'common/helpers';
import logo from 'assets/icons/hmLogo.svg';
// Глобус красится цветом текста — желтеет при наведении вместе с подписью.
import { ReactComponent as GlobeIcon } from 'assets/icons/hmGlobe.svg';
import HeaderCoursesMenu from './HeaderCoursesMenu';
import HomeButton from './HomeButton';
import classes from './SiteHeader.module.scss';


const LANGS = [
  { id: 'ru', short: 'Рус', title: 'Русский' },
  { id: 'ky', short: 'Кыр', title: 'Кыргызча' },
];

// Пункты меню из макета. Внутренние ведут по роутеру, внешние — на свои домены.
const useNavItems = () => {
  const { t } = useTranslation();

  return [
    { id: 'courses', label: t('Courses'), to: PATHS.allcourses, paths: [ PATHS.course ], mega: true },
    {
      id: 'about',
      label: t('AboutUs'),
      to: PATHS.aboutUs,
      submenu: [
        { id: 'advantages', label: t('Advantages'), to: PATHS.advantages },
        { id: 'after', label: t('AfterCourses'), to: PATHS.afterCourses },
      ],
    },
    { id: 'news', label: t('News'), to: PATHS.news },
    { id: 'binance', label: 'BinanceGeeks', to: PATHS.binanceGeeks },
    { id: 'junior', label: 'GeeksJunior', to: PATHS.geeksJunior },
    { id: 'pro', label: 'GeeksPro', to: PATHS.careerCenter },
    { id: 'studio', label: 'Geekstudio', href: externalRecourse.geekstudio },
    { id: 'online', label: 'GeeksOnline', href: ONLINE_PLATFORM_URL },
  ];
};

// Раздел подсвечен, если открыта его страница или одна из вложенных:
// «О нас» горит и на «Преимуществах», «Курсы» — и на странице курса.
const isPathIn = (pathname, to) =>
  !!to && (pathname === to || pathname.startsWith(`${to.replace(/\/$/, '')}/`));

const isSectionActive = (item, pathname) =>
  isPathIn(pathname, item.to) ||
  (item.paths ?? []).some((to) => isPathIn(pathname, to)) ||
  (item.submenu ?? []).some((sub) => isPathIn(pathname, sub.to));

// Стрелка рисуется линией цвета текста, поэтому желтеет вместе с пунктом при наведении.
const Chevron = ({ isOpen }) => (
  <svg
    className={`${classes.chevron} ${isOpen ? classes.chevronUp : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeLinecap="square" strokeLinejoin="round" />
  </svg>
);

const NavItemLink = ({ item, onClick, isSection = false }) =>
  item.href ? (
    <a
      className={classes.link}
      href={item.href}
      target="_blank"
      rel="nofollow noreferrer"
      onClick={onClick}
    >
      {item.label}
    </a>
  ) : (
    <NavLink
      className={({ isActive }) => `${classes.link} ${isActive || isSection ? classes.linkActive : ''}`}
      to={item.to}
      onClick={onClick}
    >
      {item.label}
    </NavLink>
  );

const SiteHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navItems = useNavItems();

  const [ openMenu, setOpenMenu ] = useState(null);
  // Закрытое меню остаётся в DOM, пока не доиграет анимация исчезновения.
  const [ closingMenu, setClosingMenu ] = useState(null);
  const [ prevOpenMenu, setPrevOpenMenu ] = useState(null);
  const [ isMobileOpen, setIsMobileOpen ] = useState(false);
  // Курсы в мобильном меню подгружаем при первом открытии и дальше не убираем,
  // чтобы при закрытии они плавно исчезали вместе с меню.
  const [ wasMobileOpened, setWasMobileOpened ] = useState(false);
  if (isMobileOpen && !wasMobileOpened) setWasMobileOpened(true);
  const headerRef = useRef(null);

  const currentLang = localStorage.getItem('lang') || 'ru';
  const activeLang = LANGS.find((item) => item.id === currentLang) ?? LANGS[ 0 ];

  // Закрывающееся меню вычисляем прямо при рендере, а не в эффекте:
  // иначе на один кадр оно пропадает из DOM и потом появляется снова — меню мерцает.
  if (prevOpenMenu !== openMenu) {
    setPrevOpenMenu(openMenu);
    if (prevOpenMenu) setClosingMenu(prevOpenMenu);
  }

  const closeAll = () => {
    setOpenMenu(null);
    setIsMobileOpen(false);
  };

  // Любой переход закрывает и выпадающие списки, и мобильное меню.
  useEffect(() => {
    setOpenMenu(null);
    setIsMobileOpen(false);
  }, [ pathname ]);

  // Пока открыто меню курсов или мобильное меню, страница под ним не прокручивается.
  // Ширину пропавшего скроллбара возвращаем отступом, чтобы вёрстка не прыгала.
  const isPageLocked = openMenu === 'courses' || isMobileOpen;
  useEffect(() => {
    if (!isPageLocked) return undefined;

    const { body } = document;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [ isPageLocked ]);

  // Клик мимо открытого выпадающего списка (или мимо шапки для бургер-меню)
  // и Escape закрывают меню.
  useEffect(() => {
    if (!openMenu && !isMobileOpen) return undefined;

    const onPointerDown = (event) => {
      const { target } = event;
      if (openMenu && !target.closest?.(`[data-menu="${openMenu}"]`)) setOpenMenu(null);
      if (isMobileOpen && !headerRef.current?.contains(target)) setIsMobileOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      closeAll();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ openMenu, isMobileOpen ]);

  const toggleMenu = (id) => setOpenMenu((prev) => (prev === id ? null : id));

  const isMenuShown = (id) => openMenu === id || closingMenu === id;

  // Класс анимации и обработчик её конца для выпадающего блока меню.
  const menuMotion = (id) => ({
    'data-menu': id,
    onAnimationEnd: (event) => {
      if (event.target !== event.currentTarget) return;
      if (openMenu !== id) setClosingMenu((prev) => (prev === id ? null : prev));
    },
  });
  const closingClass = (id) => (openMenu !== id ? classes.menuClosing : '');

  const renderChevron = (isOpen) => <Chevron isOpen={isOpen} />;
  const dropdownLinkClass = ({ isActive }) =>
    `${classes.dropdownLink} ${isActive ? classes.dropdownLinkActive : ''}`;

  return (
    <header className={classes.header} ref={headerRef}>
      <div className={classes.container}>
        <div className={classes.bar}>
          <button
            type="button"
            className={classes.logo}
            onClick={() => navigate(PATHS.main)}
            aria-label="Geeks"
          >
            <img src={logo} alt="Geeks" width="120" height="24" />
          </button>

          <nav className={classes.nav}>
            {navItems.map((item) => {
              if (item.mega) {
                return (
                  <button
                    type="button"
                    className={`${classes.link} ${classes.linkWithIcon} ${
                      openMenu === item.id ? classes.linkOpen : ''
                    } ${isSectionActive(item, pathname) ? classes.linkActive : ''}`}
                    key={item.id}
                    data-menu={item.id}
                    onClick={() => toggleMenu(item.id)}
                    aria-expanded={openMenu === item.id}
                  >
                    {item.label}
                    {renderChevron(openMenu === item.id)}
                  </button>
                );
              }

              if (item.submenu) {
                return (
                  <div className={classes.dropdown} key={item.id} data-menu={item.id}>
                    <button
                      type="button"
                      className={`${classes.link} ${classes.linkWithIcon} ${
                        openMenu === item.id ? classes.linkOpen : ''
                      } ${isSectionActive(item, pathname) ? classes.linkActive : ''}`}
                      onClick={() => toggleMenu(item.id)}
                      aria-expanded={openMenu === item.id}
                    >
                      {item.label}
                      {renderChevron(openMenu === item.id)}
                    </button>

                    {isMenuShown(item.id) && (
                      <div
                        className={`${classes.dropdownMenu} ${closingClass(item.id)}`}
                        {...menuMotion(item.id)}
                      >
                        <NavLink className={dropdownLinkClass} to={item.to} end>
                          {item.label}
                        </NavLink>
                        {item.submenu.map((sub) => (
                          <NavLink className={dropdownLinkClass} to={sub.to} key={sub.id}>
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return <NavItemLink item={item} key={item.id} />;
            })}
          </nav>

          <div className={classes.actions}>
            <div className={classes.dropdown} data-menu="lang">
              <button
                type="button"
                className={`${classes.link} ${classes.linkWithIcon} ${
                  openMenu === 'lang' ? classes.linkOpen : ''
                }`}
                onClick={() => toggleMenu('lang')}
                aria-expanded={openMenu === 'lang'}
              >
                <GlobeIcon className={classes.globe} aria-hidden />
                {activeLang.short}
              </button>

              {isMenuShown('lang') && (
                <div
                  className={`${classes.dropdownMenu} ${classes.dropdownMenuRight} ${closingClass('lang')}`}
                  {...menuMotion('lang')}
                >
                  {LANGS.map((lang) => (
                    <button
                      type="button"
                      className={`${classes.dropdownLink} ${
                        lang.id === activeLang.id ? classes.dropdownLinkActive : ''
                      }`}
                      key={lang.id}
                      onClick={() => changeLang(lang.id)}
                    >
                      {lang.title}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <HomeButton href={BITRIX.course} className={classes.signUp}>
              {t('SignUp')}
            </HomeButton>

            <button
              type="button"
              className={classes.burger}
              onClick={() => setIsMobileOpen((prev) => !prev)}
              aria-expanded={isMobileOpen}
              aria-controls="site-menu"
              aria-label={isMobileOpen ? 'Закрыть меню' : 'Открыть меню'}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Раскрывающееся меню курсов — во всю ширину контейнера, поверх страницы. */}
        {isMenuShown('courses') && (
          <div className={`${classes.mega} ${closingClass('courses')}`} {...menuMotion('courses')}>
            <HeaderCoursesMenu onNavigate={closeAll} />
          </div>
        )}

        {/* Мобильное меню из макета: курсы в две колонки и блок с тестом,
            под ними — остальные разделы сайта. */}
        <div
          id="site-menu"
          className={`${classes.mobileMenu} ${isMobileOpen ? classes.mobileMenuOpen : ''}`}
        >
          {wasMobileOpened && <HeaderCoursesMenu mobile onNavigate={closeAll} />}

          <nav className={classes.mobileNav}>
            {navItems.filter((item) => !item.mega).map((item) => (
              <div className={classes.mobileItem} key={item.id}>
                <NavItemLink item={item} onClick={closeAll} isSection={isSectionActive(item, pathname)} />

                {item.submenu?.map((sub) => (
                  <NavLink
                    className={({ isActive }) =>
                      `${classes.mobileSubLink} ${isActive ? classes.mobileSubLinkActive : ''}`}
                    to={sub.to}
                    key={sub.id}
                    onClick={closeAll}
                  >
                    {sub.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <HomeButton href={BITRIX.course} className={classes.mobileSignUp}>
            {t('SignUp')}
          </HomeButton>
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
